import type { IMenuProps, IOmit } from '@sixui/ui';
import { useCallback } from 'react';
import { faPen, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Menu, MenuItem } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IIapPoll } from '~/lib/api/modules/IapPolls';
import { useNavigate } from '~/components/utils/Navigation';
import { useDeleteIapPollMutation } from '~/lib/api/modules/IapPolls/hooks/useDeleteIapPollMutation';
import { useUpdateIapPollMutation } from '~/lib/api/modules/IapPolls/hooks/useUpdateIapPollMutation';

export type IIapPollListItemMenuProps = IContainerProps &
  IOmit<IMenuProps, 'trigger' | 'children'> & {
    iapPoll: IIapPoll;
  };

export const IapPollListItemMenu: React.FC<IIapPollListItemMenuProps> = (
  props,
) => {
  const { sx, iapPoll, ...other } = props;
  const { t } = useTranslation();
  const deleteIapPollMutation = useDeleteIapPollMutation();
  const updateIapPollMutation = useUpdateIapPollMutation();
  const navigate = useNavigate();

  const processing =
    deleteIapPollMutation.isLoading || updateIapPollMutation.isLoading;

  const handleDelete = useCallback((): void => {
    if (deleteIapPollMutation.isLoading) {
      return;
    }

    // eslint-disable-next-line no-alert
    const doDeletion = confirm(
      t('IapPollListItemMenu.dialog.confirmDeletion.text'),
    );

    if (doDeletion) {
      void deleteIapPollMutation
        .mutateAsync({
          appId: iapPoll.appId,
          iapPollId: iapPoll._id,
        })
        .then(() => navigate(`/apps/${iapPoll.appId}/iapPolls`))
        .catch(() => {
          // TODO: handle error (snackbar)
        });
    }
  }, [deleteIapPollMutation, iapPoll.appId, iapPoll._id, navigate, t]);

  return (
    <Menu
      trigger={({ getProps }) => (
        <IconButton
          icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
          loading={processing}
          {...getProps()}
        />
      )}
      placement="bottom-end"
      sx={sx}
      {...other}
    >
      <MenuItem
        label={t('common.edit')}
        leadingIcon={<FontAwesomeIcon icon={faPen} />}
        href={`/apps/${iapPoll.appId}/iapPolls/${iapPoll._id}/update`}
      />
      <MenuItem
        label={t('common.delete')}
        leadingIcon={<FontAwesomeIcon icon={faTrashCan} />}
        onClick={handleDelete}
        variant="danger"
      />
    </Menu>
  );
};
